import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useUser } from '@clerk/clerk-react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import type { InvitePromo } from '@arthelper/shared/referral-share';
import { InviteFriendModal } from '~/components/InviteFriendModal';

/**
 * THE APP-WIDE INVITE CAPABILITY (client copy of town-square's InviteModalProvider).
 *
 * One `InviteFriendModal` mounted once under the Router, and one imperative way to pop it:
 * `useInviteModal().open(source, promo?)`. Drop an "invite a friend" button anywhere in the
 * client app and it pops this modal — no per-surface `useState` + modal boilerplate. It also
 * owns the UNIVERSAL `?invite=1` deep link, so `/network?invite=1`, `/notifications?invite=1`
 * (any client route) drop a signed-in member straight into "type a friend's email / copy your
 * link."
 *
 * Same shape as the town-square provider, but React-Router-native: `useSearchParams` +
 * `setSearchParams(..., { replace: true })` instead of the App Router equivalents.
 */

type InviteModalValue = {
  /** Pop the invite modal. `source` names the surface (Amplitude); `promo` targets a quest. */
  open: (source: string, promo?: InvitePromo) => void;
};

// INERT default so `useInviteModal()` never throws in a tree without the provider — no-op there.
const INERT: InviteModalValue = { open: () => {} };

const InviteModalContext = createContext<InviteModalValue>(INERT);

export function useInviteModal(): InviteModalValue {
  return useContext(InviteModalContext);
}

// Module-level latch: the deep-link effect strips `?invite=1` first (replace navigation) and
// opens on the NEXT render (param gone) so the URL is clean before the modal shows. The latch
// carries "we're mid-strip for this path" across those two renders.
let pendingInviteAt: string | null = null;

function sourceForPath(pathname: string): string {
  const seg = pathname.split('/').filter(Boolean)[0];
  return `deep-link:${seg || 'home'}`;
}

export function InviteModalProvider({ children }: { children: ReactNode }) {
  const { user, isLoaded } = useUser();
  const location = useLocation();
  const pathname = location.pathname;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState<{ open: boolean; source: string; promo?: InvitePromo }>({
    open: false,
    source: '',
  });

  const open = useCallback((source: string, promo?: InvitePromo) => {
    setState({ open: true, source, promo });
  }, []);

  // Universal `?invite=1` handler — mirrors the town-square provider. Strip via the Router (not
  // `history.replaceState`) so react-router's search state stays in sync. Wait for a signed-in
  // user so a logged-out email CTA keeps the flag through Clerk sign-in. The strip reads the live
  // URL, not this render's snapshot: a child effect in the same commit may already have replaced
  // it (a sparklink landing strips its own params), and the snapshot would put those back.
  const strippingRef = useRef(false);
  useEffect(() => {
    if (!isLoaded || !user) return;
    if (searchParams.get('invite') === '1') {
      if (strippingRef.current) return;
      strippingRef.current = true;
      const live = window.location;
      pendingInviteAt = live.pathname;
      const next = new URLSearchParams(live.search);
      next.delete('invite');
      const search = next.toString();
      navigate({ pathname: live.pathname, search: search ? `?${search}` : '', hash: live.hash }, { replace: true });
      return;
    }

    strippingRef.current = false;
    if (pendingInviteAt !== pathname) return;

    pendingInviteAt = null;
    open(sourceForPath(pathname));
  }, [isLoaded, navigate, open, pathname, searchParams, user]);

  const value = useMemo<InviteModalValue>(() => ({ open }), [open]);

  return (
    <InviteModalContext.Provider value={value}>
      {children}
      <InviteFriendModal
        open={state.open}
        onOpenChange={(open) => setState((s) => ({ ...s, open }))}
        source={state.source}
        promo={state.promo}
      />
    </InviteModalContext.Provider>
  );
}
